import { Button, ButtonGroup, LegacyStack, Modal, Select, Text, TextField } from '@shopify/polaris';
import { useContext, useEffect, useMemo, useState } from 'react';
import ToastContext from '../../../../context/ToastContext';
import { SET_ERROR, SET_OK } from '../../../../context/ToastReducer';
import UserContext from '../../../../context/UserContext';
import { NEW_FILE_FAILED, NEW_FILE_LOADED, NEW_FILE_LOADING } from '../../../../context/UserReducer';
import {
  getReportsEfficacyExcel,
  getReportsEfficacySeriesPdf,
  getReportsEfficacySummaryPdf,
} from '../../../../services/reports/sellsService';
import { buildExportParams, triggerBlobDownload } from './downloads';

// Etiqueta humana de la temporada — gemelo del helper que usa el back
// en `pdfEfficacyShared.ts` (`Verano 2026` / `Invierno 2025-26`).
const seasonLabel = (season, year) =>
  season === 'VERANO' ? `Verano ${year}` : `Invierno ${year}-${String((Number(year) + 1) % 100).padStart(2, '0')}`;

// Toma las primeras 4 letras de cada valor (en mayúsculas, sin acentos).
// Sirve para resumir filtros largos en el nombre del archivo.
const shortenFilterValues = (values) =>
  (values || [])
    .map((v) =>
      String(v || '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^a-zA-Z0-9]/g, '')
        .toUpperCase()
        .slice(0, 4),
    )
    .filter(Boolean);

// Compone el filename por defecto. Patrón:
//   eficacia_<kind>_<season><year>[_<filtros abreviados>]
// Si tras añadir filtros se pasa de 80 chars, trunca con `...`.
const buildDefaultFilename = ({ kind, season, year, validFilters, depositsValue, depositRolesValue, depositGroupsValue }) => {
  const base = `eficacia_${kind}_${String(season || '').toLowerCase()}${year || ''}`;
  const parts = [];
  if (validFilters?.genders) parts.push(...shortenFilterValues(validFilters.genders.split(':')));
  if (validFilters?.categories) parts.push(...shortenFilterValues(validFilters.categories.split(':')));
  if (validFilters?.subcategories) parts.push(...shortenFilterValues(validFilters.subcategories.split(':')));
  if (depositRolesValue) parts.push(...String(depositRolesValue).split(':').filter(Boolean));
  if (depositGroupsValue)
    parts.push(...String(depositGroupsValue).split(':').filter(Boolean).map((id) => `g${id}`));
  if (depositsValue && depositsValue !== 'ALL') {
    // Para depósitos sólo abreviamos los IDs (ya son cortos).
    parts.push(...String(depositsValue).split(':').filter(Boolean));
  } else if (depositsValue === 'ALL') {
    parts.push('ALL');
  }
  const suffix = parts.length ? `_${parts.join('_')}` : '';
  const full = `${base}${suffix}`;
  return full.length > 80 ? `${full.slice(0, 77)}...` : full;
};

// Etiquetas humanas por modo — se usan para el título del modal y para
// la columna "tipo" en la lista de descargas de la campanita.
const MODE_LABELS = { analisis: 'PDF Análisis', series: 'PDF por Series', excel: 'Excel' };

// Campos de ordenación para el PDF por Series.
const SERIES_SORT_FIELDS = [
  { label: 'Serie', value: 'family' },
  { label: 'Ventas (total)', value: 'sellUnitsTotal' },
  { label: 'Entrada en tienda', value: 'firstStoreEntryDate' },
  { label: 'PVP', value: 'pvpUnit' },
  { label: 'Uds. compradas', value: 'buyUnits' },
  { label: 'Valor compra', value: 'buyValue' },
  { label: 'Valor venta (PVF)', value: 'sellValuePvfTotal' },
  { label: 'Eficacia total', value: 'efficacyTotal' },
  { label: 'Eficacia en campaña', value: 'efficacyInSeason' },
];

/**
 * Modal de exportación del informe de Eficacia. Soporta 3 modos:
 *   - 'analisis' (PDF Análisis): toggles de capítulos.
 *   - 'series' (PDF por Series): selector de orden + dirección.
 *   - 'excel': sólo título/subtítulo/filename.
 *
 * Todos los modos comparten título (por defecto "Informe Eficacia"),
 * subtítulo (por defecto "<temporada> <año>") y nombre de fichero
 * auto-generado con los filtros abreviados.
 */
export const ExportModal = ({ open, mode, onClose, exportParamsArgs }) => {
  const [, dispatchUser] = useContext(UserContext);
  const [, dispatchToast] = useContext(ToastContext);
  const params = useMemo(() => (exportParamsArgs ? buildExportParams(exportParamsArgs) : null), [exportParamsArgs]);

  const defaultSubtitle = useMemo(
    () => (exportParamsArgs ? seasonLabel(exportParamsArgs.season, exportParamsArgs.year) : ''),
    [exportParamsArgs],
  );

  const defaultFilename = useMemo(() => {
    if (!exportParamsArgs) return '';
    const kindMap = { analisis: 'analisis', series: 'series', excel: 'datos' };
    return buildDefaultFilename({
      kind: kindMap[mode] || mode,
      season: exportParamsArgs.season,
      year: exportParamsArgs.year,
      validFilters: exportParamsArgs.validFilters,
      depositsValue: exportParamsArgs.depositsValue,
      depositRolesValue: exportParamsArgs.depositRolesValue,
      depositGroupsValue: exportParamsArgs.depositGroupsValue,
    });
  }, [exportParamsArgs, mode]);

  const [title, setTitle] = useState('Informe Eficacia');
  const [subtitle, setSubtitle] = useState(defaultSubtitle);
  const [filename, setFilename] = useState(defaultFilename);
  const [includeCover, setIncludeCover] = useState(true);
  const [includeContable, setIncludeContable] = useState(true);
  const [includeCategoria, setIncludeCategoria] = useState(true);
  const [includeAlmacen, setIncludeAlmacen] = useState(true);
  const [sortBy, setSortBy] = useState('family');
  const [sortDir, setSortDir] = useState('asc');
  const [byDeposit, setByDeposit] = useState(false);
  const [byDeposits, setByDeposits] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Cuando abrimos el modal o cambia el modo, reseteamos defaults.
  useEffect(() => {
    if (!open) return;
    setTitle('Informe Eficacia');
    setSubtitle(defaultSubtitle);
    setFilename(defaultFilename);
    setIncludeCover(true);
    setIncludeContable(true);
    setIncludeCategoria(true);
    setIncludeAlmacen(true);
    setSortBy('family');
    setSortDir('asc');
    setByDeposit(false);
    setByDeposits(false);
  }, [open, mode, defaultSubtitle, defaultFilename]);

  if (!open || !params) return null;

  const handleExport = async () => {
    setDownloading(true);

    // Registramos la descarga en la lista de la campanita (`UserContext`).
    // El `id` lleva timestamp para que dos exports del mismo filename en
    // la misma sesión no choquen.
    const fileId = `${filename}-${Date.now()}`;
    dispatchUser({
      type: NEW_FILE_LOADING,
      payload: { id: fileId, type: MODE_LABELS[mode] || mode, name: title || filename, date: new Date() },
    });
    dispatchToast({ type: SET_OK, payload: { message: 'Generando export, puede tardar' } });
    onClose();

    try {
      const base = { ...params, title, subtitle, filename };
      let resp;
      let ext;
      if (mode === 'analisis') {
        resp = await getReportsEfficacySummaryPdf({
          ...base,
          includeCover: includeCover ? 'true' : 'false',
          includeContable: includeContable ? 'true' : 'false',
          includeCategoria: includeCategoria ? 'true' : 'false',
          includeAlmacen: includeAlmacen ? 'true' : 'false',
          byDeposits: byDeposits ? 'true' : 'false',
        });
        ext = 'pdf';
      } else if (mode === 'series') {
        resp = await getReportsEfficacySeriesPdf({ ...base, sortBy, sortDir });
        ext = 'pdf';
      } else {
        resp = await getReportsEfficacyExcel({ ...base, byDeposit: byDeposit ? 'true' : 'false' });
        ext = 'xlsx';
      }
      const finalName = filename.endsWith(`.${ext}`) ? filename : `${filename}.${ext}`;

      // Blob URL — vive en memoria del navegador mientras dura la sesión
      // (se revoca al refrescar la pestaña). Sirve tanto para la
      // descarga automática como para el link de la campanita y para
      // abrirlo en una pestaña nueva.
      const blobUrl = URL.createObjectURL(resp.data);
      triggerBlobDownload(resp.data, finalName);
      // Intenta abrirlo también en una pestaña nueva. Algunos
      // navegadores bloquean si tarda mucho desde el click original,
      // por eso lo intentamos sin asumir éxito.
      window.open(blobUrl, '_blank', 'noopener');

      dispatchUser({ type: NEW_FILE_LOADED, payload: { id: fileId, url: blobUrl } });
      dispatchToast({ type: SET_OK, payload: { message: 'Export generado' } });
    } catch (e) {
      dispatchUser({ type: NEW_FILE_FAILED, payload: { id: fileId } });
      dispatchToast({
        type: SET_ERROR,
        payload: { message: e?.response?.data?.message || 'Error generando el export' },
      });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Exportar — ${MODE_LABELS[mode] || ''}`}
      primaryAction={{ content: 'Exportar', onAction: handleExport, loading: downloading }}
      secondaryActions={[{ content: 'Cancelar', onAction: onClose, disabled: downloading }]}
    >
      <Modal.Section>
        <LegacyStack vertical>
          <TextField label="Nombre del fichero" value={filename} onChange={setFilename} autoComplete="off" />
          <TextField label="Título" value={title} onChange={setTitle} autoComplete="off" />
          <TextField label="Subtítulo" value={subtitle} onChange={setSubtitle} autoComplete="off" />
        </LegacyStack>
      </Modal.Section>

      {mode === 'analisis' && (
        <Modal.Section>
          <LegacyStack vertical>
            <Text variant="bodyMd" fontWeight="semibold">
              Capítulos a incluir
            </Text>
            <ButtonGroup spacing="tight">
              <Button primary={includeCover} onClick={() => setIncludeCover((v) => !v)}>
                Portada
              </Button>
              <Button primary={includeContable} onClick={() => setIncludeContable((v) => !v)}>
                Resultado económico
              </Button>
              <Button primary={includeCategoria} onClick={() => setIncludeCategoria((v) => !v)}>
                Categoría
              </Button>
              <Button primary={includeAlmacen} onClick={() => setIncludeAlmacen((v) => !v)}>
                Estado de almacén
              </Button>
            </ButtonGroup>
            <Text variant="bodyMd" fontWeight="semibold">
              Comparativa por centros
            </Text>
            <ButtonGroup spacing="tight">
              <Button primary={byDeposits} onClick={() => setByDeposits((v) => !v)}>
                Dividir por depósitos
              </Button>
            </ButtonGroup>
            <Text variant="bodySm" tone="subdued">
              Si está activo, Se repiten el análisis por categoría y el estado de almacén para cada depósito por
              separado.
            </Text>
          </LegacyStack>
        </Modal.Section>
      )}

      {mode === 'excel' && (
        <Modal.Section>
          <LegacyStack vertical>
            <Text variant="bodyMd" fontWeight="semibold">
              Agrupación
            </Text>
            <ButtonGroup spacing="tight">
              <Button primary={byDeposit} onClick={() => setByDeposit((v) => !v)}>
                Agrupar por depósito
              </Button>
              <Text variant="bodySm" tone="subdued">
                Si está activo, añade la columna Depósito y desglosa cada serie por depósito.
              </Text>
            </ButtonGroup>
          </LegacyStack>
        </Modal.Section>
      )}

      {mode === 'series' && (
        <Modal.Section>
          <LegacyStack vertical>
            <Text variant="bodyMd" fontWeight="semibold">
              Orden
            </Text>
            <Select label="Ordenar por" options={SERIES_SORT_FIELDS} value={sortBy} onChange={setSortBy} />
            <div>
              <Text variant="bodyMd" tone="subdued" as="span">
                Dirección
              </Text>
              <div style={{ marginTop: 6 }}>
                <ButtonGroup segmented>
                  <Button primary={sortDir === 'asc'} onClick={() => setSortDir('asc')}>
                    Ascendente
                  </Button>
                  <Button primary={sortDir === 'desc'} onClick={() => setSortDir('desc')}>
                    Descendente
                  </Button>
                </ButtonGroup>
              </div>
            </div>
          </LegacyStack>
        </Modal.Section>
      )}
    </Modal>
  );
};
